import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  CONSENT_STORAGE_KEY,
  CONSENT_MAX_AGE_MS,
  OPEN_CONSENT_EVENT,
  type ConsentChoice,
  consentTransition,
  makeChoice,
  parseStoredConsent,
} from "@/lib/consent";
import {
  applyRuntimeConsent,
  applyExternalConsent,
  ensureGtmLoaded,
  saveChoice,
} from "@/lib/consent-runtime";

function readChoice(): ConsentChoice | null {
  try {
    return parseStoredConsent(window.localStorage.getItem(CONSENT_STORAGE_KEY), Date.now());
  } catch {
    return null;
  }
}

function isQa() {
  try {
    return new URLSearchParams(window.location.search).get("consent_qa") === "1";
  } catch {
    return false;
  }
}

function loadIfAllowed(choice: ConsentChoice | null) {
  ensureGtmLoaded(window as never, document as never, window.location.hostname, choice, {
    qa: isQa(),
    log: (m) => console.info(m),
  });
}

const gtmLoaded = () => (window as { __seoevoGtmLoaded?: boolean }).__seoevoGtmLoaded === true;

export function ConsentManager() {
  const [ready, setReady] = useState(false);
  const [choice, setChoice] = useState<ConsentChoice | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [draft, setDraft] = useState({ analytics: false, ads: false });
  const choiceRef = useRef<ConsentChoice | null>(null);

  // Expiry must revoke on an idle open page too, not only on the next navigation/storage event.
  useEffect(() => {
    if (!choice) return;
    let timer: ReturnType<typeof setTimeout>;
    const check = () => {
      const left = choice.ts + CONSENT_MAX_AGE_MS + 1 - Date.now();
      if (left > 0) { timer = setTimeout(check, Math.min(left, 2147483647)); return; }
      choiceRef.current = null; setChoice(null);
      applyExternalConsent({ win: window as never, doc: document as never, host: window.location.hostname,
        gtmLoaded: gtmLoaded(), reload: () => window.location.reload(), load: loadIfAllowed }, choice, null);
    };
    check();
    return () => clearTimeout(timer);
  }, [choice]);

  useEffect(() => {
    const c = readChoice();
    choiceRef.current = c;
    setChoice(c);
    setReady(true);
    applyRuntimeConsent(window as never, document as never, window.location.hostname, c);
    loadIfAllowed(c);

    const onOpen = () => {
      const cur = choiceRef.current;
      setDraft({ analytics: cur?.analytics ?? false, ads: cur?.ads ?? false });
      setPanelOpen(true);
    };
    // Another tab changed the choice: it is already persisted, so a loaded GTM reloads cleanly.
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== CONSENT_STORAGE_KEY) return;
      try {
        if (e.storageArea && e.storageArea !== window.localStorage) return;
      } catch { /* blocked storage: validated read below treats it as absent */ }
      const next = readChoice();
      const prev = choiceRef.current;
      choiceRef.current = next;
      setChoice(next);
      applyExternalConsent(
        {
          win: window as never,
          doc: document as never,
          host: window.location.hostname,
          gtmLoaded: gtmLoaded(),
          reload: () => window.location.reload(),
          load: loadIfAllowed,
        },
        prev,
        next,
      );
    };
    window.addEventListener(OPEN_CONSENT_EVENT, onOpen);
    window.addEventListener("storage", onStorage);
    // Repeat selective cleanup at page exit and BFCache restoration. The next mount also cleans
    // cookies recreated by a later unload handler; never touch authentication/necessary cookies.
    const onPageHide = () => applyRuntimeConsent(window as never, document as never, window.location.hostname, readChoice());
    const onPageShow = () => onStorage({ key: CONSENT_STORAGE_KEY } as StorageEvent);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener(OPEN_CONSENT_EVENT, onOpen);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  const decide = useCallback((analytics: boolean, ads: boolean) => {
    const prev = choiceRef.current;
    const next = makeChoice(analytics, ads, Date.now());
    let persisted = false;
    try {
      persisted = saveChoice(window.localStorage, next);
    } catch {
      persisted = false; // storage blocked: choice applies to this page view only, never a reload loop
    }
    choiceRef.current = next;
    setChoice(next);
    setPanelOpen(false);
    applyRuntimeConsent(window as never, document as never, window.location.hostname, next);
    if (consentTransition(prev, next, gtmLoaded(), persisted) === "reload") {
      // Reload so the stored choice is replayed as default→update before any tag runs.
      window.location.reload();
      return;
    }
    loadIfAllowed(next);
  }, []);

  const openPanel = () => {
    setDraft({ analytics: choice?.analytics ?? false, ads: choice?.ads ?? false });
    setPanelOpen(true);
  };

  const showBanner = ready && !choice && !panelOpen;

  return (
    <>
      {showBanner && (
        <section
          role="region"
          aria-label="Aviso de cookies"
          className="fixed inset-x-3 bottom-3 z-[60] sm:inset-x-auto sm:left-4 sm:bottom-4 sm:max-w-lg rounded-xl border border-border bg-card/95 p-4 text-card-foreground shadow-2xl backdrop-blur"
        >
          <h2 className="font-display text-base font-semibold">Tu privacidad</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Usamos cookies necesarias para que la web funcione. Con tu permiso, también usaremos cookies de
            analítica y de publicidad. Puedes cambiar tu elección cuando quieras desde «Configurar cookies» en el pie.{" "}
            <Link to="/legal/cookies" className="text-primary underline underline-offset-2">
              Política de cookies
            </Link>
          </p>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Button variant="outline" size="sm" className="h-auto min-h-11 whitespace-normal px-2 text-center leading-tight" onClick={() => decide(true, true)}>
              Aceptar todas
            </Button>
            <Button variant="outline" size="sm" className="h-auto min-h-11 whitespace-normal px-2 text-center leading-tight" onClick={() => decide(false, false)}>
              Rechazar opcionales
            </Button>
            <Button variant="outline" size="sm" className="h-auto min-h-11 whitespace-normal px-2 text-center leading-tight" onClick={openPanel}>
              Configurar
            </Button>
          </div>
        </section>
      )}

      <Dialog open={panelOpen} onOpenChange={setPanelOpen}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configurar cookies</DialogTitle>
            <DialogDescription>
              Elige qué categorías opcionales permites. Las necesarias están siempre activas. Si no activas
              ninguna, no cargamos herramientas de analítica ni de publicidad.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <ConsentRow
              id="consent-necessary"
              title="Necesarias"
              text="Imprescindibles para que la web funcione, mantener la sesión del área privada y recordar esta elección. Siempre activas."
              checked
              disabled
            />
            <ConsentRow
              id="consent-analytics"
              title="Analítica"
              text="Nos ayudan a saber cómo se usa la web (Google Analytics a través de Google Tag Manager)."
              checked={draft.analytics}
              onChange={(v) => setDraft((d) => ({ ...d, analytics: v }))}
            />
            <ConsentRow
              id="consent-ads"
              title="Publicidad"
              text="Permiten medir y mejorar nuestras campañas de Google Ads y personalizar anuncios."
              checked={draft.ads}
              onChange={(v) => setDraft((d) => ({ ...d, ads: v }))}
            />
          </div>

          <DialogFooter className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:space-x-0">
            <Button variant="outline" className="h-auto min-h-11 whitespace-normal text-center leading-tight" onClick={() => decide(false, false)}>
              Rechazar opcionales
            </Button>
            <Button variant="outline" className="h-auto min-h-11 whitespace-normal text-center leading-tight" onClick={() => decide(draft.analytics, draft.ads)}>
              Guardar selección
            </Button>
            <Button variant="outline" className="h-auto min-h-11 whitespace-normal text-center leading-tight" onClick={() => decide(true, true)}>
              Aceptar todas
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ConsentRow(props: {
  id: string;
  title: string;
  text: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
      <div>
        <label htmlFor={props.id} className="text-sm font-semibold">
          {props.title}
        </label>
        <p id={`${props.id}-desc`} className="mt-1 text-xs text-muted-foreground">
          {props.text}
        </p>
      </div>
      <Switch
        id={props.id}
        checked={props.checked}
        disabled={props.disabled ?? false}
        aria-describedby={`${props.id}-desc`}
        onCheckedChange={(v) => props.onChange?.(v)}
      />
    </div>
  );
}
